// frontend/src/pages/BuilderPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  TouchSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import ComponentPalette from '@/components/builder/ComponentPalette';
import PageCanvas from '@/components/builder/PageCanvas';
import componentRegistry, { resolveRegistryEntry } from '@/registry/componentRegistry';
import { RegistryEntry } from '@/types/component';
import { savePageConfig, getPageConfig, deletePageConfig, NewPageConfig } from '@/services/builderService';
import { Save, Loader2, Trash2, LogOut, Eye, EyeOff } from 'lucide-react';
import PropertiesPanel from '@/components/builder/PropertiesPanel';
import { nanoid } from 'nanoid';


export interface CanvasItem extends RegistryEntry {
  instanceId: string;
  props: Record<string, any>;
}

export default function BuilderPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [canvasItems, setCanvasItems] = useState<CanvasItem[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pageName, setPageName] = useState('New Page');
  const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
  const [showJourneyPrompt, setShowJourneyPrompt] = useState(false);
  const [draftPageName, setDraftPageName] = useState('');
  const [isPropertiesPanelOpen, setIsPropertiesPanelOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Delete states
  const [showDeleteSlider, setShowDeleteSlider] = useState(false);
  const [deleteSliderValue, setDeleteSliderValue] = useState(0);
  const pressTimer = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (pageId) {
      setIsLoading(true);
      getPageConfig(Number(pageId))
        .then(config => {
          // Components may be stored with a __<index> suffix to uniquely
          // identify multiple instances of the same component on a page
          // (e.g. "LOS_TextBlock_100__0", "LOS_TextBlock_100__1").
          // resolveRegistryEntry strips the suffix for the registry lookup.
          const items = config.components
            .map(instanceKey => {
                const entry = resolveRegistryEntry(instanceKey);
                if (!entry) return null;
                // For components with no API getter/setter (e.g. LOS_TextBlock_100),
                // restore their saved config from PageConfig.layout[instanceKey] so
                // the builder shows the previously typed heading/content when
                // re-opening the page.
                const hasGetter = !!entry.metadata.api?.getter;
                const hasSetter = !!entry.metadata.api?.setter;
                const savedLayoutProps =
                    !hasGetter && !hasSetter && config.layout?.[instanceKey]
                        ? config.layout[instanceKey]
                        : {};
                return {
                    ...entry,
                    instanceId: nanoid(),
                    // Store the original instanceKey so confirmSave can use it
                    // as the layout key and component ID.
                    _instanceKey: instanceKey,
                    props: {
                        ...(entry.metadata.validation?.defaultValues || {}),
                        ...savedLayoutProps,
                    },
                } as CanvasItem & { _instanceKey: string };
            })
            .filter((item): item is CanvasItem & { _instanceKey: string } => item !== null);
          setCanvasItems(items as CanvasItem[]);
          setPageName(config.name);
        })
        .catch(err => console.error("Failed to load page:", err))
        .finally(() => setIsLoading(false));
    } else {
        setIsLoading(false);
    }
  }, [pageId]);

  const handleUpdateProps = (instanceId: string, newProps: any) => {
    setCanvasItems(items => items.map(item => 
        item.instanceId === instanceId ? { ...item, props: newProps } : item
    ));
  };

  const handleRemoveItem = (instanceId: string) => {
    setCanvasItems(items => items.filter(item => item.instanceId !== instanceId));
    if (selectedInstanceId === instanceId) {
      setSelectedInstanceId(null);
      setIsPropertiesPanelOpen(false);
    }
  };

  async function handleSave() {
    if (canvasItems.length === 0) {
      alert("Please add at least one component to the canvas before saving.");
      return;
    }
    
    setDraftPageName(pageName);
    setIsNameDialogOpen(true);
  }

  async function confirmSave() {
    setIsNameDialogOpen(false);
    
    if (!draftPageName) {
      return;
    }
    setPageName(draftPageName);

    setIsSaving(true);

    // Build suffix-keyed component IDs so multiple instances of the same
    // component on a page each get a unique key:
    //   ["LOS_TextBlock_100", "LOS_TextBlock_100"]
    //   → ["LOS_TextBlock_100__0", "LOS_TextBlock_100__1"]
    //
    // Components that appear only once keep their plain registry ID.
    const idCount: Record<string, number> = {};
    const idIndex: Record<string, number> = {};
    canvasItems.forEach(item => {
      idCount[item.metadata.id] = (idCount[item.metadata.id] || 0) + 1;
    });

    const instanceKeys: string[] = canvasItems.map(item => {
      const baseId = item.metadata.id;
      if (idCount[baseId] > 1) {
        const idx = idIndex[baseId] ?? 0;
        idIndex[baseId] = idx + 1;
        return `${baseId}__${idx}`;
      }
      return baseId;
    });

    // Collect per-component layout config for components that have no API setter
    // (e.g. LOS_TextBlock_100 stores its heading/content here so JourneyPage can
    // seed localState without an API call).
    const componentLayoutConfig: Record<string, any> = {};
    canvasItems.forEach((item, i) => {
      const hasSetter = !!item.metadata.api?.setter;
      const hasGetter = !!item.metadata.api?.getter;
      if (!hasSetter && !hasGetter && item.props && Object.keys(item.props).length > 0) {
        componentLayoutConfig[instanceKeys[i]] = item.props;
      }
    });

    const pageConfig: NewPageConfig = {
      name: draftPageName,
      app_type: 'LOS',
      components: instanceKeys, // suffix-keyed so each instance is unique
      layout: componentLayoutConfig,
    };

    try {
      const savedPage = await savePageConfig(pageConfig);
      setPageName(savedPage.name); // Ensure the name is updated from the server response
      setShowJourneyPrompt(true);
      
      // If this was a new page, navigate to its URL so that subsequent actions (like delete) work
      if (!pageId) {
        navigate(`/stitch/builder/${savedPage.id}`, { replace: true });
      }
    } catch (error) {
      alert("Failed to save the page. Please check the console for errors.");
    } finally {
      setIsSaving(false);
    }
  }

  const startPress = (e: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    if (!pageId || showDeleteSlider) return;
    
    // Prevent default context menu on long press for mobile
    // e.preventDefault(); 
    
    console.log("Press started on page name...");
    pressTimer.current = setTimeout(() => {
      console.log("Long press detected, showing delete slider.");
      setShowDeleteSlider(true);
    }, 800);
  };

  const cancelPress = () => {
    if (pressTimer.current) {
      console.log("Press cancelled.");
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
    };
  }, []);

  const handleSliderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    console.log("Slider value changed:", val);
    setDeleteSliderValue(val);
    
    if (val >= 100) {
      if (window.confirm(`Are you sure you want to delete "${pageName}"? This action cannot be undone.`)) {
        try {
          if (pageId) {
            console.log("Deleting page:", pageId);
            await deletePageConfig(Number(pageId));
            alert('Page deleted successfully.');
            navigate('/stitch/flow-builder');
          }
        } catch (error) {
          console.error("Delete failed:", error);
          alert('Failed to delete the page.');
          setDeleteSliderValue(0);
          setShowDeleteSlider(false);
        }
      } else {
        setDeleteSliderValue(0);
        setShowDeleteSlider(false);
      }
    }
  };

  const handleSliderRelease = () => {
    if (deleteSliderValue < 100) {
      setDeleteSliderValue(0);
    }
  };

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) return;
    
    // Check if dragging from palette (IDs in registry)
    const isFromPalette = !!componentRegistry[active.id as string];

    if (isFromPalette) {
      const newComponentId = active.id as string;
      const componentEntry = componentRegistry[newComponentId];
      if (componentEntry) {
         const newCanvasItem: CanvasItem = {
            ...componentEntry,
            instanceId: nanoid(),
            props: componentEntry.metadata.validation?.defaultValues || {},
         };

         setCanvasItems((items) => {
           // If dropped over an existing item, insert after it
           if (over.id !== 'page-canvas') {
             const overIndex = items.findIndex(i => i.instanceId === over.id);
             if (overIndex !== -1) {
               const newItems = [...items];
               newItems.splice(overIndex + 1, 0, newCanvasItem);
               return newItems;
             }
           }
           // Otherwise append
           return [...items, newCanvasItem];
         });
      }
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    if (activeId !== overId && overId !== 'page-canvas') {
      setCanvasItems((items) => {
        const oldIndex = items.findIndex(item => item.instanceId === activeId);
        const newIndex = items.findIndex(item => item.instanceId === overId);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }
  
  const selectedItem = canvasItems.find(item => item.instanceId === selectedInstanceId) || null;

  useEffect(() => {
    if (selectedItem) {
      const hasSchema = selectedItem.metadata.validation && selectedItem.metadata.validation.schema;
      setIsPropertiesPanelOpen(!!hasSchema);
    } else {
      setIsPropertiesPanelOpen(false);
    }
  }, [selectedItem]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isLoading) {
    return (
        <div className="flex justify-center items-center h-screen">
            <Loader2 className="h-12 w-12 animate-spin text-gray-500" />
        </div>
    );
  }

  return (
    <DndContext 
      sensors={sensors}
      onDragEnd={handleDragEnd} 
      collisionDetection={closestCenter}
    >
      <div className="flex flex-col h-screen bg-gray-100 overflow-hidden relative">
        {/* Top Header */}
        <header className="bg-white border-b px-4 py-3 flex justify-between items-center z-10 shadow-sm relative">
          <div className="flex items-center gap-4">
            <div 
              onPointerDown={startPress}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onContextMenu={(e) => e.preventDefault()} // Disable right click context menu
              style={{ touchAction: 'none' }}
              className={`cursor-pointer select-none transition-opacity duration-300 ${showDeleteSlider ? 'opacity-0 pointer-events-none absolute' : 'opacity-100 relative'}`}
              title={pageId ? "Press and hold to delete" : "Save page to enable deletion"}
            >
              <h1 className="text-xl font-bold text-gray-800">{pageName}</h1>
              <p className="text-xs text-gray-500">Drag components into the phone mock to build your page</p>
            </div>

            {showDeleteSlider && (
              <div className="flex items-center gap-3 bg-red-50 p-2 rounded-lg border border-red-200 shadow-sm w-80 absolute left-4 z-20">
                <div className="relative h-8 flex-1 bg-red-200 rounded-full flex items-center overflow-hidden">
                  <div 
                    className="absolute left-0 h-full bg-red-500 transition-all pointer-events-none" 
                    style={{ width: `${deleteSliderValue}%` }}
                  ></div>
                  <span className="absolute w-full text-center text-xs font-bold text-red-900 pointer-events-none z-10 opacity-70">
                    Slide to right to delete
                  </span>
                  <input 
                    type="range" 
                    min="0" 
                    max="100" 
                    value={deleteSliderValue}
                    onChange={handleSliderChange}
                    onPointerUp={handleSliderRelease}
                    className="w-full h-full opacity-0 cursor-pointer z-20"
                  />
                  <div 
                    className="absolute h-6 w-6 bg-white rounded-full shadow-md z-10 flex items-center justify-center pointer-events-none"
                    style={{ left: `calc(${deleteSliderValue}% - ${(deleteSliderValue / 100) * 24}px)`, transition: 'none' }}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </div>
                </div>
                <button 
                  onClick={() => { setShowDeleteSlider(false); setDeleteSliderValue(0); }} 
                  className="text-gray-500 hover:text-gray-800 text-xs font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Preview toggle */}
            <button
              onClick={() => setIsPreviewMode(v => !v)}
              className={[
                'flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors border',
                isPreviewMode
                  ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
              ].join(' ')}
              title={isPreviewMode ? 'Exit preview — back to edit mode' : 'Preview — see exactly what the user sees'}
            >
              {isPreviewMode ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {isPreviewMode ? 'Exit Preview' : 'Preview'}
            </button>
            <button
              onClick={() => {
                if (canvasItems.length > 0) {
                  if (window.confirm("You have unsaved changes. Are you sure you want to exit?")) {
                    navigate('/stitch/flow-builder');
                  }
                } else {
                  navigate('/stitch/flow-builder');
                }
              }}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Exit
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center px-4 py-2 bg-intellend-green text-white rounded-md shadow-sm hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Page'}
            </button>
          </div>
        </header>

        {/* Main Content Area (80% height) */}
        <div className="flex flex-1 overflow-hidden w-full">
          <ComponentPalette />
          
          <main className="w-[70%] overflow-y-auto flex items-start justify-center p-1 pb-32 relative bg-gray-100">
            {/* Phone Mock */}
            <div className="w-[375px] h-[667px] bg-white rounded-[3rem] border-[12px] border-gray-900 shadow-2xl relative flex flex-col">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20">
                <div className="w-32 h-6 bg-gray-900 rounded-b-2xl"></div>
              </div>
              
              {/* Preview mode badge inside phone */}
              {isPreviewMode && (
                <div className="absolute top-7 inset-x-0 z-10 flex justify-center pointer-events-none">
                  <span className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full shadow">
                    PREVIEW MODE
                  </span>
                </div>
              )}

              {/* Canvas Container */}
              <div className="flex-1 mt-8 mb-4 overflow-y-auto custom-scrollbar px-1">
                <PageCanvas
                    items={canvasItems}
                    selectedInstanceId={isPreviewMode ? null : selectedInstanceId}
                    onSelectItem={isPreviewMode ? () => {} : setSelectedInstanceId}
                    onRemoveItem={isPreviewMode ? () => {} : handleRemoveItem}
                    onUpdateProps={isPreviewMode ? undefined : handleUpdateProps}
                    isPreviewMode={isPreviewMode}
                />
              </div>
            </div>
          </main>
        </div>

        {/* Properties Panel Overlay */}
        <div 
          className={`absolute bottom-0 left-0 right-0 z-30 transition-transform duration-300 ease-in-out ${isPropertiesPanelOpen ? 'translate-y-0' : 'translate-y-full'}`}
        >
          {/* Toggle Button */}
          <button
            onClick={() => setIsPropertiesPanelOpen(!isPropertiesPanelOpen)}
            className="absolute -top-7 left-1/2 -translate-x-1/2 bg-white border-t border-l border-r border-gray-200 rounded-t-lg px-6 py-1 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] text-xs font-semibold text-gray-600 flex items-center hover:bg-gray-50 focus:outline-none"
          >
            {isPropertiesPanelOpen ? '▼ Hide Properties' : '▲ Show Properties'}
          </button>

          {/* Properties Panel (Bottom 25vh) */}
          <div className="h-[25vh] min-h-[200px] border-t-2 border-gray-200 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] overflow-hidden">
            <PropertiesPanel item={selectedItem} onUpdateProps={handleUpdateProps} />
          </div>
        </div>

        {/* Save Name Dialog */}
        {isNameDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
              <h2 className="text-lg font-semibold mb-4">Save Page</h2>
              <p className="text-sm text-gray-600 mb-2">Please enter a name for your page:</p>
              <input
                type="text"
                value={draftPageName}
                onChange={(e) => setDraftPageName(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && confirmSave()}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsNameDialogOpen(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSave}
                  className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded"
                  disabled={!draftPageName.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post Save Journey Prompt */}
        {showJourneyPrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-[450px] max-w-full">
              <h2 className="text-xl font-bold mb-3 text-gray-800">Page Saved Successfully!</h2>
              <p className="text-gray-600 mb-6">
                Your page "{pageName}" has been saved. Would you like to go to the Journey Flow Builder to link it into a user flow, or stay here to keep editing?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowJourneyPrompt(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors"
                >
                  Keep Editing
                </button>
                <button
                  onClick={() => navigate('/stitch/flow-builder')}
                  className="px-4 py-2 text-white bg-intellend-green hover:bg-green-700 rounded font-medium transition-colors"
                >
                  Go to Journey Builder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndContext>
  );
}
